import { useMemo } from "react";
import useQuery from "hooks/useQuery";
import OneRepMaxService from "services/OneRepMaxService";
import type { Variants } from "types";
import type { OneRepMaxDateRange, OneRepMaxValue } from "types/onerepmax";

const EMPTY_FALLBACK_DATA: OneRepMaxValue[] = [];

const cacheKeyPrefix = "OneRepMaxService.getByAthleteId";

/** Matches every cached 1RM list of an athlete, whatever the exercise or range. */
export const getAthleteOneRepMaxesCacheKeyMatcher = (athleteId: string) => (key: unknown) =>
  Array.isArray(key) && key[0] === cacheKeyPrefix && key[1] === athleteId;

interface GetAthleteOneRepMaxesQueryArgs {
  athleteId: string;
  exerciseId: string;
  dateRange: OneRepMaxDateRange;
  /** Every variant is requested at once; without variants only bilateral 1RMs are returned. */
  variants?: Variants[];
}

/**
 * Fetches an athlete's manual 1RMs for one exercise within a date range.
 * Without variants only bilateral 1RMs are returned; with variants every side comes back in one list.
 */
function useGetAthleteOneRepMaxesQuery(args: GetAthleteOneRepMaxesQueryArgs) {
  const { athleteId, exerciseId, dateRange, variants } = args;

  const key = useMemo(() => {
    if (!athleteId || !exerciseId) return null;
    return [cacheKeyPrefix, athleteId, exerciseId, dateRange, ...(variants ?? [])] as const;
  }, [athleteId, exerciseId, dateRange, variants]);

  return useQuery(
    key,
    ([, aid, eid, range, ...variants]) =>
      OneRepMaxService.getByAthleteId(aid, {
        exerciseId: eid,
        ...range,
        variants: variants.length ? variants : undefined,
      }),
    { fallbackData: EMPTY_FALLBACK_DATA }
  );
}

export default useGetAthleteOneRepMaxesQuery;
